import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { Loader2Icon } from "lucide-react";
import * as React from "react";
import { cn } from "../cn";
const buttonBaseClassName = "inline-flex select-none items-center justify-center whitespace-nowrap rounded-full font-title font-medium transition-[color,background-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";
// Outline-bearing variants draw their edge with an INSET box-shadow (paint-only)
// so thickening on hover causes no layout shift on auto-width buttons.
const buttonVariantClassNames = {
    outline: "bg-white text-primary shadow-[inset_0_0_0_1px_hsl(var(--color-neutral-700))] hover:text-accent hover:shadow-[inset_0_0_0_2px_hsl(var(--color-brand-electric-blue))] active:bg-brand active:text-primary-inverse active:shadow-[inset_0_0_0_2px_hsl(var(--color-brand-dark-blue))]",
    primary: "bg-[hsl(var(--color-brand-medium-blue))] text-primary-inverse hover:bg-brand-highlight active:bg-brand",
    secondary: "bg-[hsl(var(--color-neutral-900)/0.05)] text-primary hover:bg-[hsl(var(--color-neutral-900)/0.11)] active:bg-[hsl(var(--color-neutral-900)/0.17)]",
    ghost: "text-primary hover:bg-[hsl(var(--color-neutral-900)/0.05)] active:bg-[hsl(var(--color-neutral-900)/0.11)]",
    destructive: "bg-white text-danger shadow-[inset_0_0_0_1px_hsl(var(--color-danger-700))] hover:shadow-[inset_0_0_0_2px_hsl(var(--color-danger-700))] active:bg-danger-solid active:text-white",
    accent: "relative isolate text-primary before:absolute before:inset-0 before:rounded-full before:[background:var(--gradient-accent)] before:content-[''] before:-z-10 after:absolute after:inset-[1.5px] after:rounded-full after:bg-white after:content-[''] after:-z-10 hover:text-accent hover:after:inset-[2px] active:before:[background:hsl(var(--color-brand-dark-blue))] active:after:hidden active:text-white",
};
const buttonSizeClassNames = {
    sm: "h-8 px-3 text-xs gap-1", // 32px · 12px pad · 12px text · 4px gap
    md: "h-10 px-4 text-sm gap-1.5", // 40px · 16px pad · 14px text · 6px gap (default)
    lg: "h-11 px-5 text-base gap-2", // 44px · 20px pad · 16px text · 8px gap
};
// On the adornment side the padding is trimmed one step — Lucide icons have
// built-in air, so symmetric padding looks pushed away from the icon.
const iconSidePaddingClassNames = {
    sm: { left: "pl-2", right: "pr-2" },
    md: { left: "pl-3", right: "pr-3" },
    lg: { left: "pl-4", right: "pr-4" },
};
const iconOnlySizeClassNames = {
    sm: "size-8",
    md: "size-10",
    lg: "size-11",
};
// Labelled icon glyphs track the label at ~text+2px (defer to the label);
// icon-only glyphs are larger so they fill ~50% of the circle.
const labelledIconGlyphSizeClassNames = {
    sm: "[&_svg]:size-3.5",
    md: "[&_svg]:size-4",
    lg: "[&_svg]:size-[18px]",
};
const iconOnlyGlyphSizeClassNames = {
    sm: "[&_svg]:size-4",
    md: "[&_svg]:size-5",
    lg: "[&_svg]:size-5",
};
/**
 * Class builder, exported for the rare consumer (e.g. third-party components
 * like react-day-picker) that needs button styling on a non-button element.
 */
export function buttonVariants({ className, variant = "outline", size = "md", iconOnly = false, } = {}) {
    const v = variant ?? "outline";
    const s = size ?? "md";
    return cn(buttonBaseClassName, buttonVariantClassNames[v], iconOnly ? iconOnlySizeClassNames[s] : buttonSizeClassNames[s], iconOnly
        ? iconOnlyGlyphSizeClassNames[s]
        : labelledIconGlyphSizeClassNames[s], className);
}
function isIconOnly(props) {
    return "icon" in props && props.icon !== undefined;
}
const Button = React.forwardRef((props, ref) => {
    if (isIconOnly(props)) {
        const { icon, variant = "outline", size = "md", loading = false, trackingName, disabled, className, ...domProps } = props;
        return (_jsx("button", { className: buttonVariants({
                variant,
                size,
                iconOnly: true,
                className,
            }), "data-dd-action-name": trackingName, disabled: disabled || loading, ref: ref, type: domProps.type ?? "button", ...domProps, children: loading ? _jsx(Loader2Icon, { className: "animate-spin" }) : icon }));
    }
    const { children, leftIcon, rightIcon, asChild = false, variant = "outline", size = "md", loading = false, trackingName, disabled, className, ...domProps } = props;
    const Comp = asChild ? Slot : "button";
    const hasLeading = Boolean(leftIcon) || loading;
    const hasTrailing = Boolean(rightIcon);
    const sidePadding = iconSidePaddingClassNames[size];
    return (_jsxs(Comp, { className: buttonVariants({
            variant,
            size,
            // Padding overrides come before `className` so consumers can still
            // override; tailwind-merge resolves the per-side `pl-*`/`pr-*`
            // against the symmetric `px-*` from the size.
            className: cn(hasLeading && sidePadding.left, hasTrailing && sidePadding.right, className),
        }), "data-dd-action-name": trackingName, disabled: asChild ? undefined : disabled || loading, ref: ref, ...domProps, children: [loading && _jsx(Loader2Icon, { className: "animate-spin" }), !loading && leftIcon && (_jsx("span", { className: "inline-flex shrink-0", children: leftIcon })), _jsx(Slottable, { children: children }), rightIcon && _jsx("span", { className: "inline-flex shrink-0", children: rightIcon })] }));
});
Button.displayName = "Button";
export { Button };
